import { logJSError, counter } from '../csm/csm';
import { traverse } from '../utils/traverse';

export enum LogLevel {
    DEBUG = 'DEBUG',
    INFO = 'INFO',
    WARN = 'WARN',
    ERROR = 'ERROR',
    FATAL = 'FATAL',
    CXFATAL = 'CXFATAL'
};

export interface Logger {
    debug: (...args: any[]) => Logger,
    info: (...args: any[]) => Logger,
    warn: (...args: any[]) => Logger,
    error: (...args: any[]) => Logger,
    fatal: (...args: any[]) => Logger,
    cxfatal: (...args: any[]) => Logger,
    count: (counterName: string, value: number, increment: boolean) => Logger
}

const shouldLog = (logLevel: LogLevel) => {
    switch (logLevel) {
        case LogLevel.CXFATAL:
        case LogLevel.FATAL:
        case LogLevel.ERROR:
            return true;
        case LogLevel.WARN:
        case LogLevel.INFO:
        case LogLevel.DEBUG:
            let hasDebugQuery = false;
            if (!global.__SERVER__ && window.location) {
                hasDebugQuery = window.location.search
                    .slice(1)
                    .split('&')
                    .includes('logDebug');
            }
            return global.__DEVSERVER__ || hasDebugQuery;
        default:
            return false;
    }
};

export const niceErrorReplacer = (_key: string, value: any) => {
    if (value instanceof Error) {
        return {
            ...value,
            name: value.name,
            message: value.message,
            stack: value.stack
        }
    } else {
        return value;
    }
}

export const toNiceString = (arg: unknown): string => {
    if (arg === undefined) return 'undefined';
    if (arg === null) return 'null';
    if (typeof arg === 'object') {
        try {
            return JSON.stringify(arg, niceErrorReplacer);
        } catch (e) {
            // Cyclic object, cannot serialise
        }
    }
    return (arg as any).toString();
}

const redactWords = new Set(['firstName', 'surname', 'lastName', 'email', 'initials']);
export const redact = <T>(x: T) => traverse(x, (obj) => {
    if (Array.isArray(obj)) {
        return obj;
    } else {
        const shallowCopy: { [key: string]: any } = {};
        Object.getOwnPropertyNames(obj).forEach((key: string) => {
            shallowCopy[key] = redactWords.has(key)
                ? '<**-redacted-**>'
                : obj[key]
        });
        return shallowCopy;
    }
}) as T;

const createError = (args: any[]) => new Error(args.map(toNiceString).join('\n'));

export class ClientsideLogger implements Logger {
    attribution?: string;
    constructor(attribution?: string) {
        this.attribution = attribution;
    }
    debug = (...args: any[]) => {
        shouldLog(LogLevel.DEBUG) && console.debug(...(redact(args) as any[]));
        return this;
    }
    info = (...args: any[]) => {
        shouldLog(LogLevel.INFO) && console.info(...(redact(args) as any[]));
        return this;
    }
    warn = (...args: any[]) => {
        shouldLog(LogLevel.WARN) && console.warn(...(redact(args) as any[]));
        logJSError(createError(args), {
            logLevel: LogLevel.WARN,
            attribution: this.attribution
        });
        return this;
    }
    error = (...args: any[]) => {
        shouldLog(LogLevel.ERROR) && console.error(...(redact(args) as any[]));
        logJSError(createError(args), {
            logLevel: LogLevel.ERROR,
            attribution: this.attribution
        });
        return this;
    }
    fatal = (...args: any[]) => {
        shouldLog(LogLevel.FATAL) && console.error(...(redact(args) as any[]));
        logJSError(createError(args), {
            logLevel: LogLevel.FATAL,
            attribution: this.attribution
        });
        return this;
    }
    cxfatal(...args: any[]) {
        shouldLog(LogLevel.CXFATAL) && console.error(...(redact(args) as any[]));
        logJSError(createError(args), {
            logLevel: LogLevel.FATAL,
            attribution: this.attribution
        });
        return this;
    }
    count(counterName: string, value: number, increment: boolean) {
        if (counterName.match(/^[a-zA-Z0-9.:_-]+$/) === null) {
            (this as Logger).error(`Counter ${counterName} does not match /^[a-zA-Z0-9.:_-]+$/`);
        }
        counter(counterName, value, increment);
        return this;
    }

}
